import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import {
  STATUS_ICON_LOADING,
  STATE_ICON_PAUSE,
  STATE_ICON_RUN,
  STATUS_ICON_CLOCK,
  STATE_ICON_STOP,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_MAINTENANCE,
  STATUS_ICON_CHECK_O
} from '@/common/utils/statuses';
import { Server, ServerMigration } from '@schema';
import type { MappedServerMigration } from '@/common/store/pinia/serverMigration';

export const SERVER_STATUSES = {
  ACTIVE: 'ACTIVE',
  BUILD: 'BUILD',
  HARD_REBOOT: 'HARD_REBOOT',
  MIGRATING: 'MIGRATING',
  PASSWORD: 'PASSWORD',
  REBOOT: 'REBOOT',
  REBUILD: 'REBUILD',
  RESIZE: 'RESIZE',
  REVERT_RESIZE: 'REVERT_RESIZE',
  VERIFY_RESIZE: 'VERIFY_RESIZE',
  PAUSED: 'PAUSED',
  SHELVED: 'SHELVED',
  SHELVED_OFFLOADED: 'SHELVED_OFFLOADED',
  SHUTOFF: 'SHUTOFF',
  SUSPENDED: 'SUSPENDED',
  RESCUE: 'RESCUE',
  ERROR: 'ERROR',
  SOFT_DELETED: 'SOFT_DELETED',
  UNKNOWN: 'UNKNOWN',
  LIVE_RESIZE: 'LIVE_RESIZE',
  VERIFY_LIVE_RESIZE: 'VERIFY_LIVE_RESIZE',
};

export const SERVER_TASK_STATES = {
  BLOCK_DEVICE_MAPPING: 'block_device_mapping',
  DELETING: 'deleting',
  IMAGE_BACKUP: 'image_backup',
  IMAGE_PENDING_UPLOAD: 'image_pending_upload',
  IMAGE_SNAPSHOT: 'image_snapshot',
  IMAGE_SNAPSHOT_PENDING: 'image_snapshot_pending',
  IMAGE_UPLOADING: 'image_uploading',
  MIGRATING: 'migrating',
  NETWORKING: 'networking',
  PAUSING: 'pausing',
  'POWERING-OFF': 'powering-off',
  'POWERING-ON': 'powering-on',
  REBOOT_PENDING: 'reboot_pending',
  REBOOT_PENDING_HARD: 'reboot_pending_hard',
  REBOOT_STARTED: 'reboot_started',
  REBOOT_STARTED_HARD: 'reboot_started_hard',
  REBOOTING: 'rebooting',
  REBOOTING_HARD: 'rebooting_hard',
  REBUILD_BLOCK_DEVICE_MAPPING: 'rebuild_block_device_mapping',
  REBUILD_SPAWNING: 'rebuild_spawning',
  REBUILDING: 'rebuilding',
  RESCUING: 'rescuing',
  RESIZE_CONFIRMING: 'resize_confirming',
  RESIZE_FINISH: 'resize_finish',
  RESIZE_MIGRATED: 'resize_migrated',
  RESIZE_MIGRATING: 'resize_migrating',
  RESIZE_PREP: 'resize_prep',
  RESIZE_REVERTING: 'resize_reverting',
  RESTORING: 'restoring',
  RESUMING: 'resuming',
  SCHEDULING: 'scheduling',
  SHELVING: 'shelving',
  SHELVING_IMAGE_PENDING_UPLOAD: 'shelving_image_pending_upload',
  SHELVING_IMAGE_UPLOADING: 'shelving_image_uploading',
  SHELVING_OFFLOADING: 'shelving_offloading',
  'SOFT-DELETING': 'soft-deleting',
  SPAWNING: 'spawning',
  SUSPENDING: 'suspending',
  UNPAUSING: 'unpausing',
  UNRESCUING: 'unrescuing',
  UNSHELVING: 'unshelving',
};

export const SERVER_FILTERED_TASK_STATES = [
  'block_device_mapping',
  'deleting',
  'migrating',
  'networking',
  'powering-off',
  'powering-on',
  'reboot_pending',
  'reboot_pending_hard',
  'reboot_started',
  'reboot_started_hard',
  'rebooting',
  'rebooting_hard',
  'resize_confirming',
  'resize_finish',
  'resize_migrated',
  'resize_migrating',
  'resize_prep',
  'resize_reverting',
  'restoring',
  'resuming',
  'scheduling',
  'soft-deleting',
  'spawning',
  'suspending'
];

export const SERVER_FILTERED_STATUSES = [
  'ACTIVE',
  'BUILD',
  'ERROR',
  'SHELVED',
  'SHELVED_OFFLOADED',
  'SHUTOFF',
  'SUSPENDED',
];

export const SERVER_STATUSES_GROUPS = {
  Building: ['BUILD', 'REBUILD'],
  Errored: ['ERROR', 'UNKNOWN'],
  InProgress: ['BUILD', 'HARD_REBOOT', 'MIGRATING', 'REBOOT', 'REBUILD', 'RESIZE', 'VERIFY_RESIZE', 'RESCUING', 'UNRESCUING', 'LIVE_RESIZE', 'VERIFY_LIVE_RESIZE'].concat(Object.values(SERVER_TASK_STATES)),
  IsMigrating: ['MIGRATING', 'resize_migrated', 'resize_migrating'],
  isRebootingSoft: ['reboot_pending', 'reboot_started', 'rebooting'],
  IsResizing: ['VERIFY_RESIZE', 'resize_finish', 'resize_confirming', 'resize_prep', 'resize_reverting'],
  Running: ['ACTIVE', 'LIVE_RESIZE', 'VERIFY_LIVE_RESIZE', 'RESCUE'], // radik.aksyanov: While live resize server is still running
  Active: ['ACTIVE'],
  Shelved: ['SHELVED', 'SHELVED_OFFLOADED'],
  Stopped: ['SUSPENDED', 'PAUSED', 'SHUTOFF'],
};

export const SERVER_TRANSITIONS_FINAL_STATUS_MAP = {
  // TODO: VSTOR-73331 STOPPED status is unexpected, but reported eventually. We can get rid of arrays in the map after
  stop: [Server.status.SHUTOFF, 'STOPPED'],
  resume: [Server.status.ACTIVE],
  suspend: [Server.status.SUSPENDED],
  reboot: [Server.status.ACTIVE],
  start: [Server.status.ACTIVE],
  unpause: [Server.status.ACTIVE],
  reset: [Server.status.ACTIVE],
  shelve: [Server.status.SHELVED_OFFLOADED],
  unshelve: [Server.status.ACTIVE],
  stopMigrate: [Server.status.ACTIVE],
  evacuate: [Server.status.ACTIVE],
};

export const SERVER_HISTORIES_STATUSES = {
  Error: STATUS_ICON_TIMES_O,
  Unknown: STATUS_ICON_TIMES_O,
  'In progress': STATUS_ICON_LOADING,
  Success: STATUS_ICON_CHECK_O
};

export const SERVER_HISTORIES_STATUSES_TEXT = computedLocale(() => ({
  Error: i18n.t('Failed'),
  Unknown: i18n.t('Failed'),
  'In progress': i18n.t('In progress'),
  Success: i18n.t('Success')
}));

export const SERVER_HISTORIES_ACTIONS = computedLocale(() => ({
  attach_interface: i18n.t('Attach network interface to virtual machine'),
  attach_volume: i18n.t('Attach volume to virtual machine'),
  changePassword: i18n.t('Change password for virtual machine'),
  confirmResize: i18n.t('Confirm resize of virtual machine'),
  create: i18n.t('Create virtual machine'),
  createBackup: i18n.t('Back up virtual machine'),
  createImage: i18n.t('Create compute image from volume'),
  delete: i18n.t('Delete virtual machine'),
  detach_interface: i18n.t('Detach network interface from virtual machine'),
  detach_volume: i18n.t('Detach volume from virtual machine'),
  evacuate: i18n.t('Evacuate virtual machine'),
  extend_volume: i18n.t('Expand compute volume'),
  'live-migration': i18n.t('Live migrate virtual machine'),
  live_migration_cancel: i18n.t('Cancel live migration of virtual machine'),
  live_migration_force_complete: i18n.t('Force complete live migration of virtual machine'),
  lock: i18n.t('Lock virtual machine'),
  migrate: i18n.t('Migrate virtual machine'),
  pause: i18n.t('Pause virtual machine'),
  reboot: i18n.t('Reboot virtual machine'),
  rebuild: i18n.t('Rebuild virtual machine'),
  rescue: i18n.t('Rescue virtual machine'),
  resize: i18n.t('Resize virtual machine'),
  restore: i18n.t('Restore virtual machine'),
  resume: i18n.t('Resume virtual machine'),
  revertResize: i18n.t('Revert resize of virtual machine'),
  shelve: i18n.t('Shelve virtual machine'),
  shelveOffload: i18n.t('Shelve offload virtual machine'),
  start: i18n.t('Start virtual machine'),
  stop: i18n.t('Stop virtual machine'),
  suspend: i18n.t('Suspend virtual machine'),
  swap_volume: i18n.t('Swap volume in virtual machine'),
  trigger_crash_dump: i18n.t('Generate crash report for virtual machine'),
  unlock: i18n.t('Unlock virtual machine'),
  unpause: i18n.t('Unpause virtual machine'),
  unrescue: i18n.t('Unrescue virtual machine'),
  unshelve: i18n.t('Unshelve virtual machine'),
  resetState: i18n.t('Reset state of virtual machine'),
  refresh_volume_attachment: i18n.t('Refresh volume attachment'),
  getExportingInfo: i18n.t('Check backup export'),
  startBackupExporting: i18n.t('Start backup export'),
  finishBackupExporting: i18n.t('Finish backup export'),
}));

export const SERVER_STATUSES_ICON_MAP = {
  ACTIVE: STATE_ICON_RUN,

  BUILD: STATUS_ICON_LOADING,
  HARD_REBOOT: STATUS_ICON_LOADING,
  MIGRATING: STATUS_ICON_LOADING,
  PASSWORD: STATUS_ICON_LOADING,
  REBOOT: STATUS_ICON_LOADING,
  REBUILD: STATUS_ICON_LOADING,
  RESIZE: STATUS_ICON_LOADING,
  REVERT_RESIZE: STATUS_ICON_LOADING,
  VERIFY_RESIZE: STATUS_ICON_LOADING,

  LIVE_RESIZE: STATUS_ICON_LOADING,
  VERIFY_LIVE_RESIZE: STATUS_ICON_LOADING,

  PAUSED: STATE_ICON_PAUSE,
  SHELVED: STATUS_ICON_CLOCK,
  SHELVED_OFFLOADED: STATUS_ICON_CLOCK,
  SHUTOFF: STATE_ICON_STOP,
  SUSPENDED: STATE_ICON_PAUSE,

  RESCUE: STATUS_ICON_MAINTENANCE,

  DELETED: STATUS_ICON_TIMES_O,
  ERROR: STATUS_ICON_TIMES_O,
  SOFT_DELETED: STATUS_ICON_TIMES_O,
  UNKNOWN: STATUS_ICON_TIMES_O
};

export const SERVER_STATUSES_TEXT = computedLocale(() => ({
  ACTIVE: i18n.t('Active'),
  BUILD: i18n.t('Creating'),
  DELETED: i18n.t('Deleted'),
  ERROR: i18n.t('Error'),
  HARD_REBOOT: i18n.t('Hard reboot'),
  MIGRATING: i18n.t('Migrating'),
  PASSWORD: i18n.t('Password'),
  PAUSED: i18n.t('Paused'),
  REBOOT: i18n.t('Reboot'),
  REBUILD: i18n.t('Rebuild'),
  RESCUE: i18n.t('Rescue'),
  RESIZE: i18n.t('Resize'),
  REVERT_RESIZE: i18n.t('Undo resizing'),
  SHELVED: i18n.t('Shelved'),
  SHELVED_OFFLOADED: i18n.t('Shelved offloaded'),
  SHUTOFF: i18n.t('Shut down'),
  SOFT_DELETED: i18n.t('Soft deleted'),
  SUSPENDED: i18n.t('Suspended'),
  UNKNOWN: i18n.t('Unknown'),
  VERIFY_RESIZE: i18n.t('Verify size'),
  LIVE_RESIZE: i18n.t('Resize'),
  VERIFY_LIVE_RESIZE: i18n.t('Verify size'),
}));

export const SERVER_CONSOLE_STATUSES = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  DISCONNECTING: 'disconnecting',
  RECONNECTING: 'reconnecting',
  SHUTOFF: 'shutoff'
};

export const TASK_STATE_TEXTS = computedLocale(() => ({
  block_device_mapping: i18n.t('Mapping block devices'),
  deleting: i18n.t('Deleting'),
  image_backup: i18n.t('Creating image backup'),
  image_pending_upload: i18n.t('Awaiting image upload'),
  image_snapshot: i18n.t('Creating image snapshot'),
  image_snapshot_pending: i18n.t('Awaiting image snapshot creation'),
  image_uploading: i18n.t('Uploading image'),
  migrating: i18n.t('Migrating'),
  networking: i18n.t('Networking'),
  pausing: i18n.t('Pausing'),
  'powering-off': i18n.t('Powering off'),
  'powering-on': i18n.t('Powering on'),
  reboot_pending: i18n.t('Awaiting reboot'),
  reboot_pending_hard: i18n.t('Awaiting hard reboot'),
  reboot_started: i18n.t('Reboot started'),
  reboot_started_hard: i18n.t('Hard-reboot started'),
  rebooting: i18n.t('Rebooting'),
  rebooting_hard: i18n.t('Hard-rebooting'),
  rebuild_block_device_mapping: i18n.t('Rebuilding block device mapping'),
  rebuild_spawning: i18n.t('Rebuild spawning'),
  rebuilding: i18n.t('Rebuilding'),
  rescuing: i18n.t('Rescuing'),
  resize_confirming: i18n.t('Confirming resize'),
  resize_finish: i18n.t('Finishing resize'),
  resize_migrated: i18n.t('Resize migrated'),
  resize_migrating: i18n.t('Resize migrating'),
  resize_prep: i18n.t('Preparing for resize'),
  resize_reverting: i18n.t('Reverting resize'),
  restoring: i18n.t('Restoring'),
  resuming: i18n.t('Resuming'),
  scheduling: i18n.t('Scheduling'),
  shelving: i18n.t('Shelving'),
  shelving_image_pending_upload: i18n.t('Shelving image pending upload'),
  shelving_image_uploading: i18n.t('Shelving image uploading'),
  shelving_offloading: i18n.t('Shelving offloading'),
  'soft-deleting': i18n.t('Soft-deleting'),
  spawning: i18n.t('Spawning'),
  suspending: i18n.t('Suspending'),
  unpausing: i18n.t('Unpausing'),
  unrescuing: i18n.t('Unrescuing'),
  unshelving: i18n.t('Unshelving'),
  updating_password: i18n.t('Updating password'),
}));

export const SERVER_CHART_FILTERS = {
  ALL: 'all',
  IFACES: 'ifaces',
  VOLUMES: 'volumes'
};

// TODO: move to the backend schema
export enum FLAVORS {
  TINY = 'tiny',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  XLARGE = 'xlarge'
}

export const SERVER_MIGRATION_STATUS_MAP = computedLocale<Record<ServerMigration.status, {icon: string, text: string}>>(() => ({
  [ServerMigration.status.INPROGRESS]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('In progress')
  },
  [ServerMigration.status.SUCCESS]: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Success')
  },
  [ServerMigration.status.FAILED]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Failed')
  },
  [ServerMigration.status.CANCELLED]: {
    icon: STATE_ICON_STOP,
    text: i18n.t('Canceled')
  }
}));

export const ACTIVITY_TYPE_MAP = computedLocale<Record<MappedServerMigration['activity'], string>>(
  () => ({
    livemigration: i18n.t('VM migration'),
    migration: i18n.t('Cold migration'),
    evacuation: i18n.t('Evacuation'),
    resize: i18n.t('Resize'),
  })
);
